import { useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { button, folder, useControls } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';

import { $sceneBindings, $sceneOrderedSightIds, $sceneSights } from '../../state/appState';
import {
  DEFAULT_METALNESS,
  DEFAULT_ROUGHNESS,
  type CrowProp,
} from '../../scene/props/CrowProp';
import {
  DEFAULT_FLIGHT,
  DEFAULT_PRESET,
  FLAP_WINDOW,
  FLIGHT_PRESETS,
  type FlightPresetName,
} from '../../scene/props/crowFlight';

/**
 * Leva fires every `onChange` once while it is still building the schema. A
 * handler that writes back into the panel has to sit that pass out — the inputs
 * it targets do not exist in the store yet.
 */
type ChangeContext = { initial?: boolean };

/**
 * Longest axis in scene units. Life-size would be ~0.15 (a 45 cm bird where the
 * Kaaba collider is 3.5 ≈ 10 m); 0.35 is somewhat oversized on purpose, so the
 * mesh stays readable while the look is being judged.
 */
const DEFAULT_LENGTH = 0.35;

/** How far in front of the camera "Snap to camera" drops the bird, in units. */
const SNAP_DISTANCE = 0.8;

const NO_POI = '—';

const _forward = new THREE.Vector3();
const _snapped = new THREE.Vector3();


/**
 * DEV-only Leva panel (`?crow`) for placing the experimental animated bird in a
 * splat scene. Mutations are applied in place to the live object; nothing
 * persists — reload starts clean, and `Copy` exports the pose.
 *
 * "Snap to POI" jumps the bird onto any of the scene's POI anchors, which is how
 * you park it next to the splat's own baked bird without hunting for coordinates.
 *
 * Renders `null` when the flag is off (`bindings.crow` is null) or before the
 * GLB finishes loading.
 */
export const CrowTuner = () => {
  const { bindings, sights, orderedSightIds } = useUnit({
    bindings: $sceneBindings,
    sights: $sceneSights,
    orderedSightIds: $sceneOrderedSightIds,
  });
  const crow: CrowProp | null = bindings?.crow ?? null;

  // `$sceneBindings` fires once, before the GLB resolves — so `crow.object` is
  // still null then. Storing the instance rather than a boolean also covers a
  // scene switch: the new `crow` !== `loadedCrow` until its own GLB resolves, so
  // the panel never renders sliders bound to the previous scene's bird.
  const [loadedCrow, setLoadedCrow] = useState<CrowProp | null>(null);
  useEffect(() => {
    if (!crow) return;
    let cancelled = false;
    crow.loadPromise.then(
      () => { if (!cancelled) setLoadedCrow(crow); },
      () => { /* load error is reported by createScene */ },
    );
    return () => { cancelled = true; };
  }, [crow]);

  const root = loadedCrow === crow ? crow?.object ?? null : null;

  // Read by the Replay button; a ref rather than state so toggling loop does
  // not rebuild the whole Leva schema.
  const loopRef = useRef(true);

  // Leva's setter, reached from inside the schema it belongs to. Same ref
  // indirection as `GlbDevPanel` — the closure cannot capture the binding
  // that the very same `useControls` call returns.
  const setRef = useRef<((values: Record<string, number | string>) => void) | null>(null);

  // Flight params live in a ref: the Fly button reads them, and dragging a
  // slider must not rebuild the Leva schema mid-flight.
  const flightRef = useRef({ ...DEFAULT_FLIGHT });
  const flapStartRef = useRef(FLAP_WINDOW.start);
  const flapEndRef = useRef(FLAP_WINDOW.end);

  // --- Drag gizmo ---
  const gizmoRef = useRef<TransformControls | null>(null);
  useEffect(() => {
    if (!root || !bindings) return;
    // Capture the live objects now: `bindings` fields are nulled in place on
    // scene dispose, and this cleanup may well run after that.
    const { camera, renderer, scene, controls, requestRender } = bindings;

    const gizmo = new TransformControls(camera, renderer.domElement);
    gizmo.setMode('translate');
    gizmo.setSize(0.7);
    const helper = gizmo.getHelper();
    scene.add(helper);
    gizmo.attach(root);

    // One writer per pose per frame: OrbitControls must not fight the drag.
    gizmo.addEventListener('dragging-changed', (event: { value: unknown }) => {
      controls.enabled = !(event.value as boolean);
    });
    gizmo.addEventListener('objectChange', () => {
      setRef.current?.({
        posX: +root.position.x.toFixed(3),
        posY: +root.position.y.toFixed(3),
        posZ: +root.position.z.toFixed(3),
        rotY: +root.rotation.y.toFixed(3),
      });
      // The drag mutates the scene from outside the render loop — without this
      // the bird only jumps to its new spot once something else redraws.
      requestRender();
    });

    gizmoRef.current = gizmo;
    return () => {
      gizmo.detach();
      scene.remove(helper);
      gizmo.dispose();
      gizmoRef.current = null;
      controls.enabled = true;
    };
  }, [root, bindings]);

  const poiOptions = useMemo(() => {
    const map: Record<string, { x: number; y: number; z: number } | null> = { [NO_POI]: null };
    for (const id of orderedSightIds) {
      const sight = sights[id];
      if (!sight) continue;
      map[sight.name ?? String(id)] = sight.position;
    }
    return map;
  }, [sights, orderedSightIds]);

  const initial = useMemo(() => {
    if (!root) return null;
    return {
      x: +root.position.x.toFixed(3),
      y: +root.position.y.toFixed(3),
      z: +root.position.z.toFixed(3),
      rotY: +root.rotation.y.toFixed(3),
      clips: crow?.clipNames ?? [],
    };
  }, [root, crow]);

  const [, setControls] = useControls(
    'Crow (?crow)',
    () => {
      if (!crow || !root || !initial) return {} as Schema;
      const schema: Schema = {
        gizmo: {
          label: 'gizmo', value: true,
          onChange: (v: boolean) => {
            const gizmo = gizmoRef.current;
            if (!gizmo) return;
            gizmo.enabled = v;
            gizmo.getHelper().visible = v;
            bindings?.requestRender();
          },
        },
        gizmoMode: {
          label: 'gizmo mode',
          value: 'translate',
          options: ['translate', 'rotate', 'scale'],
          onChange: (v: string) => {
            gizmoRef.current?.setMode(v as 'translate' | 'rotate' | 'scale');
            bindings?.requestRender();
          },
        },
        // Jumping to a POI must write the sliders back, or the next drag would
        // teleport the bird to the stale value still shown in the UI.
        snapTo: {
          label: 'snap to POI',
          value: NO_POI,
          options: Object.keys(poiOptions),
          onChange: (name: string) => {
            const target = poiOptions[name];
            if (!target) return;
            crow.setPosition(target.x, target.y, target.z);
            setRef.current?.({
              posX: +target.x.toFixed(3),
              posY: +target.y.toFixed(3),
              posZ: +target.z.toFixed(3),
            });
          },
        },
        posX: {
          label: 'pos X', value: initial.x, step: 0.01,
          onChange: (v: number) => { root.position.x = v; },
        },
        posY: {
          label: 'pos Y', value: initial.y, step: 0.01,
          onChange: (v: number) => { root.position.y = v; },
        },
        posZ: {
          label: 'pos Z', value: initial.z, step: 0.01,
          onChange: (v: number) => { root.position.z = v; },
        },
        rotY: {
          label: 'rot Y', value: initial.rotY, min: -Math.PI, max: Math.PI, step: 0.01,
          onChange: (v: number) => { root.rotation.y = v; },
        },
        length: {
          label: 'length (units)', value: DEFAULT_LENGTH, min: 0.01, max: 1, step: 0.005,
          onChange: (v: number) => crow.setLength(v),
        },
        // The scene has no environment map, so metalness above ~0.1 turns the
        // bird into a black silhouette. See `DEFAULT_METALNESS`.
        metalness: {
          label: 'metalness', value: DEFAULT_METALNESS, min: 0, max: 1, step: 0.01,
          onChange: (v: number) => { crow.setMetalness(v); bindings?.requestRender(); },
        },
        roughness: {
          label: 'roughness', value: DEFAULT_ROUGHNESS, min: 0, max: 1, step: 0.01,
          onChange: (v: number) => { crow.setRoughness(v); bindings?.requestRender(); },
        },
        // Off on load: the source clip is a placeholder until our own motion
        // exists. Also lets the render scheduler park while the bird sits still.
        playing: {
          label: 'playing', value: false,
          // `requestRender` or nothing happens: the scene may be parked, and a
          // parked loop never notices that `needsRerender()` turned true.
          onChange: (v: boolean) => { crow.setPlaying(v); bindings?.requestRender(); },
        },
        speed: {
          label: 'speed', value: 1, min: 0, max: 3, step: 0.05,
          onChange: (v: number) => crow.setTimeScale(v),
        },
        loop: {
          label: 'loop', value: true,
          onChange: (v: boolean) => { loopRef.current = v; },
        },
        Replay: button(() => crow.replay(loopRef.current)),
        // The bird is life-sized, so anything far away reads as a speck. Fly to
        // the framing you want to judge, then drop it right in front of you.
        'Snap to camera': button(() => {
          const cam = bindings?.camera;
          if (!cam) return;
          _forward.set(0, 0, -1).applyQuaternion(cam.quaternion);
          _snapped.copy(cam.position).addScaledVector(_forward, SNAP_DISTANCE);
          crow.setPosition(_snapped.x, _snapped.y, _snapped.z);
          setRef.current?.({
            posX: +_snapped.x.toFixed(3),
            posY: +_snapped.y.toFixed(3),
            posZ: +_snapped.z.toFixed(3),
          });
        }),
      };

      // Only offer the dropdown when the GLB actually ships several clips.
      if (initial.clips.length > 1) {
        schema.clip = {
          label: 'clip',
          value: initial.clips[0],
          options: initial.clips,
          onChange: (v: string) => crow.play(v, loopRef.current),
        };
      }

      // Takes off from wherever the bird currently stands, and the path
      // guarantees it lands back on exactly that spot.
      const flyNow = () => {
        crow.startFlight({
          ...flightRef.current,
          origin: { x: root.position.x, y: root.position.y, z: root.position.z },
        });
        bindings?.requestRender();
      };

      // Loading a preset has to push every slider back into the panel, or the
      // next drag would apply a stale value on top of the preset.
      const loadPreset = (name: FlightPresetName) => {
        const preset = FLIGHT_PRESETS[name];
        flightRef.current = { ...preset };
        setRef.current?.({
          radius: preset.radius,
          height: preset.height,
          duration: preset.duration,
          bank: preset.bank,
          climb: preset.climb,
          descend: preset.descend,
          wobble: preset.wobble,
          lobes: preset.lobes,
          bob: preset.bob,
          bobCycles: preset.bobCycles,
        });
      };

      schema.Flight = folder(
        {
          preset: {
            label: 'preset',
            value: DEFAULT_PRESET,
            options: Object.keys(FLIGHT_PRESETS),
            // Loads values only — never flies. The mount-time cache override
            // writes this input too, and taking off from there would launch the
            // bird on page load.
            onChange: (v: string, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              loadPreset(v as FlightPresetName);
            },
          },
          // Ranges are sized for a hop off a perch, not a flyover — fine steps
          // matter far more than a big ceiling here.
          radius: {
            value: DEFAULT_FLIGHT.radius, min: 0.05, max: 3, step: 0.01,
            onChange: (v: number) => { flightRef.current.radius = v; },
          },
          height: {
            label: 'height', value: DEFAULT_FLIGHT.height, min: 0.02, max: 2, step: 0.01,
            onChange: (v: number) => { flightRef.current.height = v; },
          },
          duration: {
            label: 'duration (s)', value: DEFAULT_FLIGHT.duration, min: 1, max: 20, step: 0.25,
            onChange: (v: number) => { flightRef.current.duration = v; },
          },
          // No `bearing` slider: the direction the bird leaves in comes from
          // `rot Y` / the gizmo, and `startFlight` derives the bearing from it.
          bank: {
            label: 'bank', value: DEFAULT_FLIGHT.bank, min: -1, max: 1, step: 0.01,
            onChange: (v: number) => { flightRef.current.bank = v; },
          },
          // What stops the path reading as a drawn circle.
          wobble: {
            label: 'wobble', value: DEFAULT_FLIGHT.wobble, min: 0, max: 0.9, step: 0.01,
            onChange: (v: number) => { flightRef.current.wobble = v; },
          },
          lobes: {
            label: 'lobes', value: DEFAULT_FLIGHT.lobes, min: 1, max: 6, step: 1,
            onChange: (v: number) => { flightRef.current.lobes = v; },
          },
          bob: {
            label: 'bob', value: DEFAULT_FLIGHT.bob, min: 0, max: 0.15, step: 0.005,
            onChange: (v: number) => { flightRef.current.bob = v; },
          },
          bobCycles: {
            label: 'bob cycles', value: DEFAULT_FLIGHT.bobCycles, min: 1, max: 16, step: 1,
            onChange: (v: number) => { flightRef.current.bobCycles = v; },
          },
          yawOffset: {
            label: 'yaw offset', value: DEFAULT_FLIGHT.yawOffset, min: -Math.PI, max: Math.PI, step: 0.05,
            onChange: (v: number) => { flightRef.current.yawOffset = v; },
          },
          reversed: {
            label: 'reversed', value: false,
            onChange: (v: boolean) => { flightRef.current.direction = v ? -1 : 1; },
          },
          climb: {
            label: 'climb frac', value: DEFAULT_FLIGHT.climb, min: 0.05, max: 0.5, step: 0.01,
            onChange: (v: number) => { flightRef.current.climb = v; },
          },
          descend: {
            label: 'descend frac', value: DEFAULT_FLIGHT.descend, min: 0.05, max: 0.5, step: 0.01,
            onChange: (v: number) => { flightRef.current.descend = v; },
          },
          // The source clip's flapping burst. Drag until the loop seam stops
          // showing — the measured window is a starting point, not gospel.
          flapStart: {
            label: 'flap from (s)', value: FLAP_WINDOW.start, min: 6, max: 10, step: 0.05,
            onChange: (v: number) => {
              flapStartRef.current = v;
              crow.setFlapWindow(v, flapEndRef.current);
            },
          },
          flapEnd: {
            label: 'flap to (s)', value: FLAP_WINDOW.end, min: 6, max: 10.4, step: 0.05,
            onChange: (v: number) => {
              flapEndRef.current = v;
              crow.setFlapWindow(flapStartRef.current, v);
            },
          },
          Fly: button(() => flyNow()),
          Land: button(() => {
            crow.stopFlight();
            setRef.current?.({
              posX: +root.position.x.toFixed(3),
              posY: +root.position.y.toFixed(3),
              posZ: +root.position.z.toFixed(3),
            });
            bindings?.requestRender();
          }),
        },
        { collapsed: false },
      );

      schema['Copy Transform'] = button(() => {
        const payload = JSON.stringify(
          {
            position: {
              x: +root.position.x.toFixed(4),
              y: +root.position.y.toFixed(4),
              z: +root.position.z.toFixed(4),
            },
            rotationY: +root.rotation.y.toFixed(4),
            length: +crow.length.toFixed(4),
          },
          null,
          2,
        );
        console.warn('[CrowTuner]', payload);
        navigator.clipboard.writeText(payload).then(
          () => console.warn('[CrowTuner] transform copied to clipboard'),
          (err) => console.error('[CrowTuner] clipboard write failed', err),
        );
      });

      return schema;
    },
    [crow, root, initial, poiOptions, bindings],
  );

  useEffect(() => {
    setRef.current = setControls as unknown as (values: Record<string, number | string>) => void;
  }, [setControls]);

  // Leva restores slider values from localStorage and fires `onChange` with
  // them, so a radius left over from a previous session silently wins over the
  // defaults in code — edits to `DEFAULT_FLIGHT` then appear to do nothing.
  // Re-assert ours once the bird exists, the same way `GlbDevPanel` re-asserts
  // camera fov. Position comes from the object, not from the constant, so the
  // panel always agrees with where the bird actually is.
  useEffect(() => {
    if (!root) return;
    flightRef.current = { ...FLIGHT_PRESETS[DEFAULT_PRESET] };
    flapStartRef.current = FLAP_WINDOW.start;
    flapEndRef.current = FLAP_WINDOW.end;
    (setControls as unknown as (values: Record<string, number | string>) => void)({
      posX: +root.position.x.toFixed(3),
      posY: +root.position.y.toFixed(3),
      posZ: +root.position.z.toFixed(3),
      rotY: +root.rotation.y.toFixed(3),
      length: DEFAULT_LENGTH,
      metalness: DEFAULT_METALNESS,
      roughness: DEFAULT_ROUGHNESS,
      preset: DEFAULT_PRESET,
      radius: DEFAULT_FLIGHT.radius,
      height: DEFAULT_FLIGHT.height,
      duration: DEFAULT_FLIGHT.duration,
      bank: DEFAULT_FLIGHT.bank,
      yawOffset: DEFAULT_FLIGHT.yawOffset,
      climb: DEFAULT_FLIGHT.climb,
      descend: DEFAULT_FLIGHT.descend,
      wobble: DEFAULT_FLIGHT.wobble,
      lobes: DEFAULT_FLIGHT.lobes,
      bob: DEFAULT_FLIGHT.bob,
      bobCycles: DEFAULT_FLIGHT.bobCycles,
      flapStart: FLAP_WINDOW.start,
      flapEnd: FLAP_WINDOW.end,
    });
  }, [root, setControls]);

  return null;
};
